
import React, { useState, useEffect, Suspense, useCallback } from "react";
import { useSelector } from "react-redux";
import Header from "./layout-components/header/Header";
import Sidebar from "./layout-components/sidebar/Sidebar";
import Footer from "./layout-components/footer/Footer";
import ThemeRoutes from "../routes/Router";
import Spinner from "./../views/spinner/Spinner";
import { setAuthToken } from "../services/api"
import { useKeycloak } from "@react-keycloak/web";
import { Navigate, useNavigate } from "react-router-dom";
import "../Styles/mobile-layout.css";
import "../Styles/responsive-table.scss";
import PageBackButton from "../components/PageBackButton";
import { SIDEBAR_DESKTOP_MIN_WIDTH } from "./layoutBreakpoints";

const FullLayout = (props) => {

  const [width, setWidth] = useState(window.innerWidth);
  const settings = useSelector((state) => state.settings);
  const { keycloak, initialized } = useKeycloak();
  const navigate = useNavigate();

  const closeMobileSidebar = useCallback(() => {
    const element = document.getElementById("main-wrapper");
    if (element) {
      element.classList.remove("show-sidebar");
    }
  }, []);

  useEffect(() => {
    const currentPath = window.location.pathname;
    if (currentPath !== '/' && currentPath !== '/implicit/callback') {
      sessionStorage.setItem('redirectPath', currentPath);
    }
  }, []);

  const getUserMetadata = async () => {
    try {
      await keycloak.updateToken(60);
      const token = keycloak.token;
      setAuthToken(token);

      if (!token) {
        sessionStorage.setItem('redirectPath', window.location.pathname);
        navigate('/', { replace: true });
      } else {
        const savedPath = sessionStorage.getItem('redirectPath') || '/businesses';
        sessionStorage.removeItem('redirectPath');
        navigate(savedPath, { replace: true });
      }
    } catch (e) {
      console.error('Error updating token:', e);
      sessionStorage.setItem('redirectPath', window.location.pathname);
      keycloak.logout({
        redirectUri: process.env.REACT_APP_AUTH_LOGOUTURL,
      });
    }
  };

  useEffect(() => {
    if (!initialized) {
      return;
    }
    if (keycloak.authenticated) {
      getUserMetadata(); // Fetch user metadata when authenticated
    } else {
      navigate('/'); // Redirect if not authenticated
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keycloak.authenticated, initialized]);



  useEffect(() => {
    const updateDimensions = () => {
      const element = document.getElementById("main-wrapper");
      const viewportWidth = window.innerWidth;
      setWidth(viewportWidth);
      if (!element) {
        return;
      }

      if (viewportWidth < SIDEBAR_DESKTOP_MIN_WIDTH) {
        element.setAttribute("data-sidebartype", "overlay");
        element.classList.remove("mini-sidebar");
        return;
      }

      element.classList.remove("show-sidebar");
      element.setAttribute("data-sidebartype", settings.activeSidebarType);
      element.classList.remove("mini-sidebar");
    };

    updateDimensions();
    window.addEventListener("load", updateDimensions);
    window.addEventListener("resize", updateDimensions);
    return () => {
      window.removeEventListener("load", updateDimensions);
      window.removeEventListener("resize", updateDimensions);
    };
  }, [settings.activeSidebarType]);

  useEffect(() => {
    if (width >= SIDEBAR_DESKTOP_MIN_WIDTH) {
      closeMobileSidebar();
    }
  }, [width, closeMobileSidebar]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        closeMobileSidebar();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [closeMobileSidebar]);

  if (!initialized) {
    return <Spinner />;
  }

  if (!keycloak.authenticated) {
    return <Navigate to="/" replace />;
  }

  return (
    <div
      id="main-wrapper"
      dir={settings.activeDir}
      data-theme={settings.activeTheme}
      data-layout={settings.activeThemeLayout}
      data-sidebartype={settings.activeSidebarType}
      data-sidebar-position={settings.activeSidebarPos}
      data-header-position={settings.activeHeaderPos}
      data-boxed-layout={settings.activeLayout}
    >
      {/*--------------------------------------------------------------------------------*/}
      {/* Header                                                                         */}
      {/*--------------------------------------------------------------------------------*/}
      <Header />
      {/*--------------------------------------------------------------------------------*/}
      {/* Sidebar                                                                        */}
      {/*--------------------------------------------------------------------------------*/}
      <Sidebar {...props} routes={ThemeRoutes} />
      <button
        type="button"
        className="sidebar-backdrop"
        aria-label="Close menu"
        onClick={closeMobileSidebar}
      />
      {/*--------------------------------------------------------------------------------*/}
      {/* Page Main-Content                                                              */}
      {/*--------------------------------------------------------------------------------*/}
      <div className="page-wrapper d-block">
        <div className="page-content container-fluid">
          <PageBackButton />
          <Suspense fallback={<Spinner />}>
            {props.children}
          </Suspense>
        </div>
        <Footer />
      </div>
      {/*--------------------------------------------------------------------------------*/}
      {/* Customizer from which you can set all the Layout Settings                      */}
      {/*--------------------------------------------------------------------------------*/}
    </div>
  );
}

export default FullLayout;